import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { axiosInstance } from '../../api/api';

type CollectionModalProps = {
  show: boolean;
  slot: any;
  slotIndex?: number;
  onClose: () => void;
  onSave: (slotIndex: number, values: any) => void;
};

function CollectionModal({ slot, show, slotIndex, onClose, onSave }: CollectionModalProps): JSX.Element {
  const [crawlValues, setCrawlValues] = useState<any>({
    url: '',
  });

  const [values, setValues] = useState<{
    name?: string;
    username: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
    telegram?: string;
    avatar: any;
    slots: {
      title: string;
      link: string;
      description?: string;
      image: string;
      video?: string;
    }[];
  }>({
    username: '',
    avatar: null,
    slots: [],
  });

  useEffect(() => {
    if (typeof slot === 'undefined') {
      setValues({
        username: '',
        avatar: null,
        slots: [],
      });
    } else {
      setValues(slot);
    }
    setCrawlValues({});
  }, [slotIndex]);

  const handleChange = (e: React.ChangeEvent<any>, cb?: any): void => {
    const { name, value } = e.target;

    if (cb) {
      cb([name, value]);
      return;
    }

    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleCrawlChange = (e: React.ChangeEvent<any>, cb?: any): void => {
    const { name, value } = e.target;

    if (cb) {
      cb([name, value]);
      return;
    }

    setCrawlValues((prevValues: any) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<any>, cb?: any): Promise<void> => {
    const formData = new FormData();
    const targetName = e.target.name;
    formData.append('file', e.target.files[0] as Blob);
    const response = await axiosInstance(true).post('/file-upload', formData);

    if (cb) {
      cb([targetName, response.data.payload.url]);
      return;
    }

    setValues((prevValues) => ({
      ...prevValues,
      [targetName]: response.data.payload.url,
    }));
  };

  const handleSubmitForm = (e: React.ChangeEvent<any>): void => {
    e.preventDefault();
    onSave(slotIndex!, values);
  };

  const SlotForm = useMemo(() => ({ slot, index }: { slot: any, index: number }): JSX.Element => {
    return (
      <>
        <div className="flex-row-between">
          <p>Slide {index + 1}</p>
          <Button
            variant="secondary"
            type="button"
            onClick={(): void => {
              setValues((prevValues) => ({
                ...prevValues,
                slots: prevValues.slots.filter((_, i) => i !== index),
              }));
            }}>
            Remove
          </Button>
        </div>
        <Form.Group className="mb-3" controlId="title">
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={slot?.title}
            onChange={(e): void => {
              const { name, value } = e.target;
              setValues((prevValues: any) => ({
                ...prevValues,
                slots: prevValues.slots.map((s: any, i: number) => {
                  if (i === index) {
                    return {
                      ...slot,
                      [name]: value,
                    };
                  }
                  return s;
                })
              }));
            }}
            placeholder="Enter Title"
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="link">
          <Form.Label>Link</Form.Label>
          <Form.Control
            type="text"
            name="link"
            value={slot?.link}
            onChange={(e): void => {
              const { name, value } = e.target;
              setValues((prevValues: any) => ({
                ...prevValues,
                slots: prevValues.slots.map((s: any, i: number) => {
                  if (i === index) {
                    return {
                      ...slot,
                      [name]: value,
                    };
                  }

                  return s;
                }),
              }));
            }}
            placeholder="Enter link"
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            type="text"
            name="description"
            value={slot?.description}
            onChange={(e): void => {
              const { name, value } = e.target;
              setValues((prevValues: any) => ({
                ...prevValues,
                slots: prevValues.slots.map((s: any, i: number) => {
                  if (i === index) {
                    return {
                      ...slot,
                      [name]: value,
                    };
                  }

                  return s;
                }),
              }));
            }}
            placeholder="Enter description"
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="image">
          <Form.Label>Image</Form.Label>
          {slot.image && (
            <div>
              <img src={slot.image} alt="image" width="300" height="300"/>
              <Button
                variant="secondary"
                type="button"
                onClick={(): void => {
                  setValues((prevValues: any) => ({
                    ...prevValues,
                    slots: prevValues.slots.map((s: any, i: number) => {
                      if (i === index) {
                        return {
                          ...slot,
                          image: '',
                        };
                      }

                      return s;
                    }),
                  }));
                }}>
                Remove
              </Button>
            </div>
          )}
          {!slot.image && (
            <Form.Control
              type="file"
              name="image"
              onChange={(e): Promise<void> =>
                handleFileChange(e, (response: string[]) => {
                  setValues((prevValues: any) => ({
                    ...prevValues,
                    slots: prevValues.slots.map((s: any, i: number) => {
                      if (i === index) {
                        return {
                          ...slot,
                          [response[0]]: response[1],
                        };
                      }

                      return s;
                    }),
                  }));
                })
              }
              accept="image/*"
            />
          )}
        </Form.Group>
        <Form.Group className="mb-3" controlId="video">
          <Form.Label>Video</Form.Label>
          {slot.video && (
            <div>
              <video src={slot.video} width="300" height="300" controls={true}>
                <source src={slot.video} type="video/mp4"/>
              </video>
              <Button
                variant="secondary"
                type="button"
                onClick={(): void => {
                  setValues((prevValues: any) => ({
                    ...prevValues,
                    slots: prevValues.slots.map((s: any, i: number) => {
                      if (i === index) {
                        return {
                          ...slot,
                          video: '',
                        };
                      }

                      return s;
                    }),
                  }));
                }}>
                Remove
              </Button>
            </div>
          )}
          {!slot.video && (
            <Form.Control type="file" name="video" onChange={(e): Promise<void> =>
              handleFileChange(e, (response: string[]) => {
                setValues((prevValues: any) => ({
                  ...prevValues,
                  slots: prevValues.slots.map((s: any, i: number) => {
                    if (i === index) {
                      return {
                        ...slot,
                        [response[0]]: response[1],
                      };
                    }

                    return s;
                  }),
                }));
              })
            } accept="video/*"/>
          )}
        </Form.Group>
        <hr/>
      </>
    );
  }, []);

  const handleCrawlSubmit = async (): Promise<void> => {
    if (crawlValues?.url?.length) {
      const creationId = crawlValues.url.split('/').pop();
      const response = await fetch(`https://api.vast.app/api/creations/${creationId}`, {
        method: 'GET',
      });
      const result = (await response.json()).result;
      console.log('result', result);
      const slots = JSON.parse(JSON.stringify((values && values.slots) || []));
      slots.push({
        title: result.name,
        link: crawlValues.url,
        description: result.description,
        image: result.media.preview.url,
        video: result.media?.video?.url
      });
      setValues({
        ...values,
        name: result.creators[0].name,
        username: result.creators[0].username,
        avatar: result.creators[0].profilePictureUrl,
        slots
      });
    }
  };

  const renderSlotForm = useMemo(() => (): JSX.Element[] | null => {
    if (values?.slots?.length > 0) {
      return values.slots.map((_, index) => {
        return <SlotForm key={index} slot={values.slots[index]} index={index}/>;
      });
    }
    return null;
  }, [values?.slots]);

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add collection</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="username">
          <Form.Label>Crawl url</Form.Label>
          <Form.Control
            type="text"
            name="url"
            value={crawlValues?.url}
            onChange={handleCrawlChange}
            placeholder="Enter url"
          />
          <hr/>
          <Button
            variant="secondary"
            type="button"
            onClick={handleCrawlSubmit}>
            Fetch
          </Button>
        </Form.Group>

        <Form onSubmit={handleSubmitForm}>
          <h3>Author Information</h3>
          <Form.Group className="mb-3" controlId="name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={values?.name}
              onChange={handleChange}
              placeholder="Enter name"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="username">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              name="username"
              value={values?.username}
              onChange={handleChange}
              placeholder="Enter username"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="instagram">
            <Form.Label>Instagram URL</Form.Label>
            <Form.Control
              type="text"
              name="instagram"
              value={values?.instagram}
              onChange={handleChange}
              placeholder="Enter Instagram URL"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="facebook">
            <Form.Label>Facebook URL</Form.Label>
            <Form.Control
              type="text"
              name="facebook"
              value={values?.facebook}
              onChange={handleChange}
              placeholder="Enter Facebook URL"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="youtube">
            <Form.Label>YouTube URL</Form.Label>
            <Form.Control
              type="text"
              name="youtube"
              value={values?.youtube}
              onChange={handleChange}
              placeholder="Enter YouTube URL"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="telegram">
            <Form.Label>Telegram URL</Form.Label>
            <Form.Control
              type="text"
              name="telegram"
              value={values?.telegram}
              onChange={handleChange}
              placeholder="Enter Telegram URL"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="avatar">
            <Form.Label>Avatar</Form.Label>
            {values?.avatar && (
              <div>
                <img src={values.avatar} alt="avatar" width="100" height="100"/>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={(): void => {
                    setValues((prevValues: any) => ({
                      ...prevValues,
                      avatar: '',
                    }));
                  }}>
                  Remove
                </Button>
              </div>
            )}
            {!values?.avatar && (
              <Form.Control
                type="file"
                name="avatar"
                onChange={handleFileChange}
                accept="image/*"
              />
            )}
          </Form.Group>
          <hr/>
          <h3>Slides</h3>
          {renderSlotForm()}
          <Button
            variant="secondary"
            type="button"
            onClick={(): void => {
              setValues((prevValues: any) => ({
                ...prevValues,
                slots: [...(prevValues?.slots || []), {}],
              }));
            }}>
            Add Slot
          </Button>

          <div className="d-grid gap-2 mt-4">
            <Button variant="primary" type="submit">
              Save
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default CollectionModal;
